import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "history-range-picker__title" }
const _hoisted_2 = { class: "history-range-picker__trigger-container" }
const _hoisted_3 = {
  id: "datepicker-trigger",
  class: "history-range-picker__trigger"
}

import VueDatePicker from '@vuepic/vue-datepicker';
import { endOfDay, format, isBefore, startOfDay } from 'date-fns';
import { nanoid } from 'nanoid';
import { computed, type PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { addNumberOfMonths, endOfCurrentDay, subNumberOfMonths } from '@exchange/helpers/time-offset';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { ModalFooter } from '@exchange/libs/modals/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { langsInfoService } from '@exchange/libs/utils/langs-info/src';

export interface DateFilterModel {
  from?: Date | string;
  to?: Date | string;
}


export const colorVariants = {
  light: 'light',
  dark: 'dark',
} as const;
export type ColorVariant = (typeof colorVariants)[keyof typeof colorVariants];

export default /*#__PURE__*/_defineComponent({
  __name: 'HistoryRangePicker',
  props: {
  value: { type: Object as PropType<DateFilterModel>, default: () => ({ from: undefined, to: undefined }) },
  monthsToSelect: { type: Number, default: CONSTANTS.HISTORY_DROPDOWN_NUMBER_OF_MONTHS_SELECTED },
  /**
   * Position of the datepicker relative to the trigger element,
   * except for `viewport-center` which will center the datepicker within the viewport
   */
  position: { type: String as PropType<'left' | 'center' | 'viewport-center' | 'bottom-center' | 'right'>, default: 'center' },
  onModal: { type: Boolean, default: false },
  filledTrigger: { type: Boolean, default: true },
  variant: { type: String as PropType<ColorVariant>, default: colorVariants.dark },
  hasClearButton: { type: Boolean, default: false },
  placeholder: { type: String, required: false },
},
  emits: ["update:value"],
  setup(__props, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n({ useScope: 'global' });
const { isXS } = useMQBreakpoints();

const historyRangePickerMobileWrapperId = `range-picker-${nanoid()}`;

const datepicker = ref<InstanceType<typeof VueDatePicker>>();
const pickerIsShown = ref(false);

const datePickerPosition = computed(() => (props.position.includes('-center') ? 'center' : props.position));
const teleportCenter = computed(() => props.position === 'viewport-center');
const altPosition = computed(() =>
  props.position === 'bottom-center'
    ? (el: HTMLElement) => {
        const datePickerRect = el.getBoundingClientRect();
        const top = datePickerRect.bottom + 8;
        const left = document.body.clientWidth / 2;
        const transform = 'translateX(-50%)';

        return { top, left, transform };
      }
    : null,
);

const displayedDateFormat = computed(() => CONSTANTS.HISTORY_DROPDOWN_DISPLAYED_DATE_FORMAT);
const formatDate = (d: Date) => format(d, displayedDateFormat.value);

const getDateValue = (v: string | Date | undefined) => {
  if (v === undefined) {
    return v;
  }

  return typeof v === 'string' ? new Date(v) : v;
};

const dateOne = ref(getDateValue(props.value.from));
const dateTwo = ref(getDateValue(props.value.to));
const maxDate = ref<Date>(new Date(endOfCurrentDay()));
const minDate = ref<Date | undefined>(undefined);

const dateRange = computed(() => (dateOne.value && dateTwo.value ? [dateOne.value, dateTwo.value] : null));

watch(
  () => props.value,
  () => {
    dateOne.value = getDateValue(props.value.from);
    dateTwo.value = getDateValue(props.value.to);
    maxDate.value = new Date(endOfCurrentDay());
  },
);

const getFormattedDates = (dates: Array<Date> | null) => {
  let fDates = '';

  if (dates && dates[0] && dates[1]) {
    fDates = `${formatDate(dates[0])} - ${formatDate(dates[1])}`;
  }

  return fDates;
};

const formattedDates = computed(() => getFormattedDates(dateRange.value));

const multiCalendars = computed(() => {
  if (isXS.value) return false;

  return 2;
});

const pickerHeader = computed(() => {
  const withSelectVersion = t('modules.datePicker.title');
  const justTitleVersion = t('modules.datePicker.titleShort');

  if (props.onModal) {
    return withSelectVersion;
  }

  return isXS.value ? `${justTitleVersion}:` : justTitleVersion;
});

const localeName = computed(() => langsInfoService.language.value);
const fullscreen = computed(() => isXS.value);

const onClosed = () => {
  pickerIsShown.value = false;
  emitInput();
};

const onOpened = () => {
  maxDate.value = new Date(endOfCurrentDay());
  pickerIsShown.value = true;
};

const onUpdate = (value: [Date, Date]) => {
  [dateOne.value, dateTwo.value] = value;
};

const emitInput = () => {
  if (dateOne.value && !dateTwo.value) {
    dateTwo.value = dateOne.value;
  }

  emit('update:value', {
    from: dateOne.value ? startOfDay(dateOne.value).toISOString() : undefined,
    to: dateTwo.value ? endOfDay(dateTwo.value).toISOString() : undefined,
  });
};

const internalModelChange = (value: Date[] | null) => {
  if (!value) return;

  if (value.length === 1 && value[0]) {
    const possibleMinDate = subNumberOfMonths(props.monthsToSelect, value[0]);
    const possibleMaxDate = addNumberOfMonths(props.monthsToSelect, value[0]);

    minDate.value = possibleMinDate;

    if (isBefore(possibleMaxDate, new Date(maxDate.value))) {
      maxDate.value = possibleMaxDate;
    }
  } else {
    minDate.value = undefined;
    maxDate.value = new Date();
  }
};

const selectDate = () => {
  datepicker.value?.selectDate();
};
const closeMenu = () => {
  datepicker.value?.closeMenu();
};

const clearDates = () => {
  dateOne.value = undefined;
  dateTwo.value = undefined;

  onClosed();
  closeMenu();
};

return (_ctx: any,_cache: any) => {
  const _component_ot_icon = _resolveComponent("ot-icon")!
  const _component_x_button = _resolveComponent("x-button")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["history-range-picker", [`history-range-picker--${__props.variant}`, { 'history-range-picker--full-width': __props.onModal }]])
  }, [
    _createElementVNode("div", _hoisted_1, _toDisplayString(pickerHeader.value), 1),
    (fullscreen.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          _withDirectives(_createElementVNode("div", {
            id: historyRangePickerMobileWrapperId,
            class: _normalizeClass(['history-range-picker-fullscreen-container', `history-range-picker-fullscreen-container--${__props.variant}`])
          }, null, 2), [
            [_vShow, pickerIsShown.value]
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(VueDatePicker), {
      ref_key: "datepicker",
      ref: datepicker,
      range: true,
      "model-value": dateRange.value,
      "start-date": dateTwo.value,
      offset: "6",
      "six-weeks": "center",
      "month-name-format": "long",
      dark: __props.variant !== 'light',
      "hide-offset-dates": false,
      "month-change-on-scroll": false,
      clearable: false,
      "enable-time-picker": false,
      locale: localeName.value,
      "min-date": minDate.value,
      "max-date": maxDate.value,
      "multi-calendars": multiCalendars.value,
      transitions: { menuAppear: 'ot-transition-fade' },
      position: datePickerPosition.value,
      "alt-position": altPosition.value,
      "teleport-center": teleportCenter.value,
      "menu-class-name": `${fullscreen.value ? 'history-range-picker__menu history-range-picker__menu--fullscreen' : 'history-range-picker__menu'}`,
      "calendar-class-name": "'history-range-picker__calendar",
      teleport: fullscreen.value ? `#${historyRangePickerMobileWrapperId}` : true,
      onOpen: onOpened,
      onClosed: onClosed,
      "onUpdate:modelValue": onUpdate,
      onInternalModelChange: internalModelChange
    }, {
      "calendar-icon": _withCtx(() => [
        _createVNode(_component_ot_icon, {
          class: "history-range-picker__calendar-close-icon",
          name: "calendar-tick",
          "inherit-stroke": "inherit-stroke"
        })
      ]),
      "action-row": _withCtx((menu) => [
        _createVNode(_unref(ModalFooter), { class: "history-range-picker__footer-actions" }, {
          default: _withCtx(() => [
            _createVNode(_component_x_button, {
              class: "history-range-picker__footer-actions-button",
              variant: "outline",
              text: _ctx.$t('modules.datePicker.cancel'),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (closeMenu()))
            }, null, 8, ["text"]),
            (__props.hasClearButton)
              ? (_openBlock(), _createBlock(_component_x_button, {
                  key: 0,
                  class: "history-range-picker__footer-actions-button",
                  disabled: !dateRange.value?.length,
                  variant: "outline",
                  text: _ctx.$t('modules.datePicker.clear'),
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (clearDates()))
                }, null, 8, ["disabled", "text"]))
              : _createCommentVNode("", true),
            _createVNode(_component_x_button, {
              class: "history-range-picker__footer-actions-button",
              variant: "primary",
              disabled: !menu.internalModelValue || menu.internalModelValue.length < 2,
              text: _ctx.$t('modules.datePicker.select'),
              onClick: _cache[2] || (_cache[2] = ($event: any) => (selectDate()))
            }, null, 8, ["disabled", "text"])
          ]),
          _: 2
        }, 1024)
      ]),
      trigger: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(formattedDates.value || __props.placeholder || _ctx.$t('modules.accountHistory.export.fullHistory')), 1),
          _createElementVNode("div", {
            class: _normalizeClass(["history-range-picker__trigger-arrow", { 'history-range-picker__trigger-arrow--opened': pickerIsShown.value }])
          }, [
            _createVNode(_component_ot_icon, {
              class: "history-range-picker__trigger-arrow-icon",
              name: "arrow-down",
              "inherit-stroke": "inherit-stroke"
            })
          ], 2)
        ])
      ]),
      "arrow-left": _withCtx(() => [
        _createVNode(_component_ot_icon, {
          class: "history-range-picker__month-icon",
          name: "previous",
          "inherit-stroke": "inherit-stroke"
        })
      ]),
      "arrow-right": _withCtx(() => [
        _createVNode(_component_ot_icon, {
          class: "history-range-picker__month-icon",
          name: "next",
          "inherit-stroke": "inherit-stroke"
        })
      ]),
      _: 1
    }, 8, ["model-value", "start-date", "dark", "locale", "min-date", "max-date", "multi-calendars", "position", "alt-position", "teleport-center", "menu-class-name", "teleport"])
  ], 2))
}
}

})